import { ElementRef, inject, Injectable } from "@angular/core";
import { Router } from '@angular/router';
import { BrowserApiService } from "./browser-api.service";

@Injectable({
  providedIn: 'root',
})
export class ToolsService {
  _router = inject(Router)
  _browserApi = inject(BrowserApiService)

  scrollToElement(el: HTMLElement): void {
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  }

  changeQueryParam(param: Record<string, string | null>): void {
    this._router.navigate([], {
      queryParams: param,
      queryParamsHandling: 'merge',
    });
  }

  scrollById(id: string, force?: boolean): void {
    if (this._browserApi.isServer()) return;
    setTimeout(() => {
      const element = document.getElementById(id);
      if (force) {
        element?.scrollIntoView();
      } else {
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  }

  scrollByClass(className: string): void {
    if (this._browserApi.isServer()) return;
    const elementList = document.querySelectorAll('.' + className);
    const element = elementList[0] as HTMLElement;
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  }

  scrollToTopImmediately(): void {
    if (this._browserApi.isServer()) return;
    setTimeout(() => {
      document.querySelector('body')?.scrollTo(0, 0);
    }, 200);
  }

  getFormData(event: any, file?: File): FormData | null {
    if (event) {
      const target = event.target || event.srcElement;
      if (!target?.files[0]) return null;
      file = target.files[0];
    }

    const formData: FormData = new FormData();

    if (file) formData.set('file', file, file?.name || 'name');

    return file ? formData : null;
  }

  removeDuplicates(
    arr: { display: string | undefined; value: number | undefined }[]
  ): any[] {
    const result: any[] = [];
    const ids: number[] = [];
    arr.forEach(el => {
      if (ids.includes(<number>el.value)) return;
      if (el.value != null) {
        ids.push(el.value);
      }
      result.push(el);
    });
    return result;
  }

  scrollToFirstInvalidControl(eRef: ElementRef, labelOffset = 50) {
    if (this._browserApi.isServer()) return;
    const firstInvalidControl: HTMLElement = eRef.nativeElement.querySelector(
      'form .ng-invalid, form .invalid'
    );
    if (!firstInvalidControl) return;

    setTimeout(() => {
      window.scroll({
        top: this.getTopOffset(firstInvalidControl, labelOffset),
        left: 0,
        behavior: 'smooth',
      });
    }, 100);
  }

  getTopOffset(controlEl: HTMLElement, labelOffset: number): number {
    if (this._browserApi.isServer()) return 0;
    return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  toString(el: number | string): string {
    return String(el);
  }

  banBodyScroll() {
    if (this._browserApi.isServer()) return;
    document.body.classList.add('overflow-y-hidden');
    document.body.classList.add('relative');
    document.body.style.touchAction = 'none';
  }

  allowBodyScroll() {
    if (this._browserApi.isServer()) return;
    document.body.classList.remove('overflow-y-hidden');
    document.body.classList.remove('relative');
    document.body.style.touchAction = '';
  }

  pluralize(count: number, forms: string[]): string {
    const cases = [2, 0, 1, 1, 1, 2];
    const formIndex =
      count % 100 > 4 && count % 100 < 20 ? 2 : cases[Math.min(count % 10, 5)];
    return forms[formIndex];
  }
}
